import type {
  AiConnectionStatusRow,
  CommandPlannerHostStatus,
  LaunchCatalogRow,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  RuntimeOutage,
} from './data';
import { AI_CONNECTIONS_DISPLAY, type AiConnectionDisplay } from './ai-connections-catalog';

export type MainCommandReplyTarget = 'overseer' | 'ux' | 'core';
export type ReplySourceClass = 'subscription' | 'local' | 'metered_api' | 'unknown';

/** A reply shortcut on a message. The Overseer is the one management participant that answers. */
export type MainCommandDirectRecipient =
  | { readonly kind: 'management'; readonly target: 'overseer' }
  | { readonly kind: 'worker'; readonly workerId: string };

export function directReplySelection(recipient: MainCommandDirectRecipient): {
  readonly targets: readonly MainCommandReplyTarget[];
  readonly workerIds: readonly string[];
} {
  return recipient.kind === 'management'
    ? { targets: [recipient.target], workerIds: [] }
    : { targets: [], workerIds: [recipient.workerId] };
}

export interface MainCommandReplyIdentity {
  readonly role: MainCommandReplyTarget;
  readonly roleLabel: 'Overseer' | 'Observer UX' | 'Observer Core';
  readonly provider: string;
  readonly runtime: string;
  readonly model: string;
  readonly thinking: string;
  readonly node: string;
  readonly available: boolean;
  readonly availabilityLabel: string;
  readonly sourceClass: ReplySourceClass;
  readonly sourceLabel: 'Subscription' | 'Local' | 'Metered API' | 'Unknown';
  readonly nodeId: string | null;
  readonly offeringKey: string | null;
  readonly connectionKey: string | null;
}

/** Every management target a stored message can name, in display order. Past messages keep theirs. */
export const REPLY_TARGETS: readonly MainCommandReplyTarget[] = ['overseer', 'ux', 'core'];

/**
 * The management recipients the owner can address now. Observer UX and Observer Core no
 * longer answer, so they are not offered: the Reviewer checks finished runs and is not a
 * Main Command participant.
 */
export const SELECTABLE_REPLY_TARGETS: readonly MainCommandReplyTarget[] = ['overseer'];

/** True when every currently eligible Worker is selected; with none eligible it is never on. */
export function isAllWorkersSelection(
  selectedWorkers: ReadonlySet<string>,
  eligibleWorkerIds: ReadonlySet<string>,
): boolean {
  return eligibleWorkerIds.size > 0 && [...eligibleWorkerIds].every((workerId) => selectedWorkers.has(workerId));
}

/** The All workers chip: adds every eligible Worker, or removes them all when already selected. Management is untouched. */
export function toggledAllWorkers(
  selectedWorkers: ReadonlySet<string>,
  eligibleWorkerIds: ReadonlySet<string>,
): Set<string> {
  const next = new Set(selectedWorkers);
  const allOn = isAllWorkersSelection(selectedWorkers, eligibleWorkerIds);
  for (const workerId of eligibleWorkerIds) {
    if (allOn) next.delete(workerId);
    else next.add(workerId);
  }
  return next;
}

/** One direct chip press changes only that canonical recipient. */
export function toggledReplyTargets(
  current: ReadonlySet<MainCommandReplyTarget>,
  target: MainCommandReplyTarget,
): Set<MainCommandReplyTarget> {
  const next = new Set(current);
  if (next.has(target)) next.delete(target);
  else next.add(target);
  return next;
}

export function replyTargetLabel(target: MainCommandReplyTarget): MainCommandReplyIdentity['roleLabel'] {
  if (target === 'overseer') return 'Overseer';
  return target === 'ux' ? 'Observer UX' : 'Observer Core';
}

function titleCase(value: string): string {
  return value
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function loopback(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost' || parsed.hostname === '[::1]';
  } catch {
    return false;
  }
}

function connectionDisplay(connectionKey: string | null): AiConnectionDisplay | null {
  if (connectionKey === null) return null;
  return AI_CONNECTIONS_DISPLAY.find((row) => row.connectionKey === connectionKey) ?? null;
}

function connectionStatus(
  statuses: readonly AiConnectionStatusRow[],
  nodeId: string,
  connectionKey: string,
): AiConnectionStatusRow | null {
  return statuses.find((row) => row.node_id === nodeId && row.connection_key === connectionKey) ?? null;
}

function connectedSource(
  offering: ModelOfferingRef | null,
  catalog: LaunchCatalogRow | null,
  display: AiConnectionDisplay | null,
): ReplySourceClass {
  if (offering?.execution_location === 'cloud') return 'metered_api';
  if (offering?.execution_location !== 'local') return 'unknown';
  if (display?.meterMode === 'local_unlimited') return 'local';
  if (display !== null) return 'subscription';

  // Overseer may predate AI Connections probing. A loopback runtime is still
  // truthfully local; anything else remains Unknown until a connection proves it.
  const env = catalog?.env ?? {};
  const baseUrls = Object.entries(env)
    .filter(([key, value]) => /(?:^|_)BASE_URL$/i.test(key) && typeof value === 'string')
    .map(([, value]) => String(value));
  return baseUrls.some(loopback) ? 'local' : 'unknown';
}

function connectionReady(
  status: AiConnectionStatusRow | null,
  source: ReplySourceClass,
): boolean {
  if (status === null || !status.installed) return false;
  if (source === 'local') return status.meter_mode === 'local_unlimited';
  if (source === 'subscription') return status.auth_state === 'signed_in';
  return false;
}

export function replySourceLabel(source: ReplySourceClass): MainCommandReplyIdentity['sourceLabel'] {
  if (source === 'subscription') return 'Subscription';
  if (source === 'local') return 'Local';
  if (source === 'metered_api') return 'Metered API';
  return 'Unknown';
}

/** Thinking is shown only from explicit configuration, never inferred from model names. */
export function configuredThinking(runtimeKey: string | null, catalog: readonly LaunchCatalogRow[], source: ReplySourceClass): string {
  if (runtimeKey === null) return 'Unknown';
  const row = catalog.find((item) => item.runtime_key === runtimeKey);
  const env = row?.env ?? {};
  const configured = Object.entries(env)
    .filter(([key, value]) => /(?:REASONING_EFFORT|EFFORT_LEVEL)$/i.test(key)
      && typeof value === 'string' && value.trim() !== '')
    .sort(([left], [right]) => left.localeCompare(right))[0]?.[1];
  if (typeof configured === 'string' && configured.trim() !== '') return titleCase(configured.trim());
  return source === 'local' ? 'Model default' : source === 'subscription' ? 'Provider default' : 'Unknown';
}

function unknownIdentity(role: MainCommandReplyTarget, detail = 'Unavailable'): MainCommandReplyIdentity {
  return {
    role,
    roleLabel: replyTargetLabel(role),
    provider: 'Unknown',
    runtime: 'Unknown',
    model: 'Unknown',
    thinking: 'Unknown',
    node: 'Unknown',
    available: false,
    availabilityLabel: detail,
    sourceClass: 'unknown',
    sourceLabel: 'Unknown',
    nodeId: null,
    offeringKey: null,
    connectionKey: null,
  };
}


function bestOverseerConnection(input: {
  nodeId: string;
  runtimeKey: string;
  statuses: readonly AiConnectionStatusRow[];
}): { display: AiConnectionDisplay; status: AiConnectionStatusRow } | null {
  const candidates = AI_CONNECTIONS_DISPLAY.flatMap((display) => {
    if (!display.runtimeKeys.includes(input.runtimeKey)) return [];
    const status = connectionStatus(input.statuses, input.nodeId, display.connectionKey);
    if (status === null || !status.installed) return [];
    const source: ReplySourceClass = display.meterMode === 'local_unlimited' ? 'local' : 'subscription';
    return connectionReady(status, source) ? [{ display, status }] : [];
  });
  return candidates.length === 1 ? candidates[0]! : null;
}

export function buildMainCommandReplyIdentities(input: {
  plannerHost: CommandPlannerHostStatus | null;
  nodes: readonly NodePresence[];
  nodeOfferings: readonly NodeOffering[];
  offerings: readonly ModelOfferingRef[];
  catalog: readonly LaunchCatalogRow[];
  outages: readonly RuntimeOutage[];
  connectionStatuses: readonly AiConnectionStatusRow[];
  nowMs: number;
}): Readonly<Record<MainCommandReplyTarget, MainCommandReplyIdentity>> {
  const host = input.plannerHost;
  let overseer = unknownIdentity('overseer', 'Planner Host not configured');
  if (host !== null) {
    const configuredOffering = host.configured_offering_key == null
      ? null
      : input.offerings.find((offering) => offering.key === host.configured_offering_key) ?? null;
    const candidates = configuredOffering === null ? input.nodeOfferings
      .filter((item) => item.node_id === host.node_id)
      .map((item) => input.offerings.find((offering) => offering.key === item.offering_key) ?? null)
      .filter((item): item is ModelOfferingRef => item !== null && item.runtime_key === host.runtime_key) : [];
    const unique = [...new Map(candidates.map((item) => [item.key, item])).values()];
    // A managed profile carries one exact offering. The legacy host is identifiable
    // only when its runtime has exactly one offering; ambiguity remains Unknown.
    const offering = configuredOffering ?? (unique.length === 1 ? unique[0]! : null);
    const connected = bestOverseerConnection({
      nodeId: host.node_id,
      runtimeKey: host.runtime_key,
      statuses: input.connectionStatuses,
    });
    const catalog = input.catalog.find((item) => item.runtime_key === host.runtime_key) ?? null;
    const source = offering === null
      ? 'unknown'
      : connectedSource(offering, catalog, connected?.display ?? null);
    const safe = source === 'subscription' || source === 'local';
    const connectionTruth = connected !== null || source === 'local';
    const available = host.connected && host.offered && host.runtime_available && safe && connectionTruth;
    overseer = {
      role: 'overseer',
      roleLabel: 'Overseer',
      provider: offering?.provider_label.trim() || 'Unknown',
      runtime: host.runtime_key,
      model: offering?.model_key ?? 'Unknown',
      thinking: host.configured_effort_key == null
        ? configuredThinking(host.runtime_key, input.catalog, source)
        : titleCase(host.configured_effort_key),
      node: host.node_name,
      available,
      availabilityLabel: available
        ? 'Available'
        : !safe ? 'Source Unknown or metered' : !connectionTruth ? 'Connection not proven' : 'Unavailable',
      sourceClass: source,
      sourceLabel: replySourceLabel(source),
      nodeId: host.node_id,
      offeringKey: offering?.key ?? null,
      connectionKey: connected?.display.connectionKey ?? null,
    };
  }

  // Observer UX and Observer Core were removed; a stored message that named them keeps its badge.
  return {
    overseer,
    ux: unknownIdentity('ux', 'Not configured'),
    core: unknownIdentity('core', 'Not configured'),
  };
}


export function targetBadges(targets: readonly string[] | null | undefined): readonly MainCommandReplyTarget[] {
  if (targets == null) return ['overseer'];
  return REPLY_TARGETS.filter((target) => targets.includes(target));
}

export function executionIdentityByline(identity: unknown): string | null {
  if (identity === null || typeof identity !== 'object' || Array.isArray(identity)) return null;
  const row = identity as Record<string, unknown>;
  const provider = typeof row['actualProvider'] === 'string' ? row['actualProvider'] : null;
  const runtime = typeof row['actualRuntime'] === 'string' ? row['actualRuntime'] : null;
  const model = typeof row['actualModel'] === 'string' ? row['actualModel'] : 'Unknown';
  const thinking = typeof row['actualThinking'] === 'string' ? row['actualThinking'] : 'Unknown';
  const node = typeof row['nodeName'] === 'string' ? row['nodeName'] : 'Unknown';
  const source = typeof row['sourceClass'] === 'string' ? titleCase(row['sourceClass']) : 'Unknown';
  if (provider === null && runtime === null) return null;
  const configuredRuntime = typeof row['configuredRuntime'] === 'string' ? row['configuredRuntime'] : null;
  const mismatch = row['mismatch'] === true;
  const actual = [provider ?? 'Unknown', runtime ?? 'Unknown', model, `Thinking: ${thinking}`, node, source].join(' · ');
  return mismatch && configuredRuntime !== null ? `${actual} · configured ${configuredRuntime} (mismatch)` : actual;
}
